import type {MouseEvent, ReactElement, ReactNode} from 'react';
import {useEffect, useState} from 'react';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {MODALS_IDS} from '@Components/modal-container';
import type {MoreOptionsModalProps} from '@Modals/more-options-modal/more-options-modal';
import {useAppSelector} from '@/hooks';
import useWindowSize from '@Hooks/useWindowSize';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {ActionMenu} from '@Components/action-menu/action-menu';
import {ActionMenuFlyout} from '@Components/action-menu/components/action-menu-flyout/action-menu-flyout';
import type {ActionMenuItem} from '@Components/action-menu/action-menu.types';
import {BetaTag} from '@Components/beta-tag/beta-tag';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import {EDITABLE_LAYERS_BADGE_COUNT, EditMenuScreen, type GetEditSubmenuOptions} from './useImageEditMenu.types';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useOpenChatImageInEditor} from '@Components/ai-chat-window/hooks/useOpenChatImageInEditor';
import {useMakeTextEditable} from '@Components/ai-chat-window/hooks/useMakeTextEditable';
import {showMakeTextEditableDailyLimitGrowl} from '@Components/ai-chat-window/lib/make-text-editable-growl.library';
import {isMieLayeringAvailable, LayerDecompositionModel} from '@Components/ai-chat-window/lib/make-text-editable.library';
import {LoadingStates} from '@Utils/loading.util';
import styles from './useImageEditMenu.module.scss';

interface UseImageEditMenuParams {
  chatId: string;
  hashedId: string;
  hasLayeredPoster: boolean;
  moreOptionsPanelId: string;
  isDisabled: boolean;
}

interface UseImageEditMenu {
  editScreen: EditMenuScreen;
  isEditSubmenuScreen: boolean;
  isEditMenuInModalMode: boolean;
  showEditableLayers: boolean;
  isDailyLimitReached: boolean;
  refreshDailyLimitOnMenuOpen: () => void;
  getMobileEditMenuRow: () => ReactElement;
  getEditMenuRow: () => ReactElement;
  getEditSubmenu: (options?: GetEditSubmenuOptions) => ReactElement;
  getMoreOptionsModalHeaderProps: () => Partial<MoreOptionsModalProps>;
}

export const useImageEditMenu = ({chatId, hashedId, hasLayeredPoster, moreOptionsPanelId, isDisabled}: UseImageEditMenuParams): UseImageEditMenu => {
  const [editScreen, setEditScreen] = useState<EditMenuScreen>(EditMenuScreen.MAIN);
  const {windowWidth} = useWindowSize();
  const {state, actions} = useAiChat();
  const {openPosterInEditor} = useOpenChatImageInEditor();
  const {makeTextEditable, runImageLayering} = useMakeTextEditable({chatId});
  const isEditMenuInModalMode = windowWidth < MOBILE_MODAL_WIDTH_THRESHOLD;
  const showEditableLayers = state.canUseEditableTextLayers || hasLayeredPoster;
  // Reuse is free server-side (start() short-circuits before the limit check), so the cap only gates new conversions.
  const isDailyLimitReached = state.makeTextEditableLimit.isLimitReached && !hasLayeredPoster;

  const loadingStates = state.getLoadingStatesForImageActions(chatId, hashedId);
  const isMakeEditableLoading = loadingStates.makeTextEditableLoadingState === LoadingStates.LOADING;
  const runningLayerDecompositionModel = loadingStates.makeTextEditableLayerDecompositionModel;
  const isRunningLayering = isMakeEditableLoading && runningLayerDecompositionModel !== null;
  const isTextOnlyLoading = isMakeEditableLoading && runningLayerDecompositionModel === null;

  const onOpenInEditor = (): void => {
    openPosterInEditor(hashedId);
  };

  const onMakeEditableLayers = (): void => {
    void makeTextEditable(hashedId, hasLayeredPoster);
  };

  const onImageLayeringClick = (layerDecompositionModel: LayerDecompositionModel) => (): void => {
    void runImageLayering(hashedId, layerDecompositionModel);
  };

  const refreshDailyLimitOnMenuOpen = (): void => {
    if (!showEditableLayers) {
      return;
    }
    void actions.refreshMakeTextEditableLimit();
  };

  const onEditableLayersClick = (_id?: string, event?: MouseEvent<HTMLElement>): void => {
    if (!isDailyLimitReached) {
      onMakeEditableLayers();
      return;
    }
    if (isEditMenuInModalMode) {
      event?.stopPropagation();
      showMakeTextEditableDailyLimitGrowl();
    }
  };

  const isMoreOptionsModalOpen = useAppSelector((reduxState): boolean => {
    const modal = reduxState.modals.modalsHashmap[MODALS_IDS.MORE_OPTIONS_MODAL];
    if (!modal?.isOpen) {
      return false;
    }
    return modal.panelProps?.panelId === moreOptionsPanelId;
  });

  useEffect(() => {
    if (!isMoreOptionsModalOpen) {
      setEditScreen(EditMenuScreen.MAIN);
    }
  }, [isMoreOptionsModalOpen]);

  const getEditableLayersBadge = (): ReactElement => {
    return (
      <span className={styles.badge}>
        <Icon icon="icon-flash" size={IconSize.SIZE_ICON_12} className="ai-text-dark" />
        <Text size={TextSize.SM} bold val={EDITABLE_LAYERS_BADGE_COUNT} className={`ai-text-dark ${styles.badgeCount}`} />
      </span>
    );
  };

  const getEditableLayersInfoNote = (): ReactNode => {
    if (!showEditableLayers || isDailyLimitReached) {
      return undefined;
    }
    return (
      <div className={styles.infoNote}>
        <Icon icon="icon-info" size={IconSize.SIZE_ICON_16} className={styles.infoIcon} />
        <Text size={TextSize.SM} val={window.i18next.t('pmwjs_editable_layers_info')} className={styles.infoText} />
      </div>
    );
  };

  const getEditableLayersRightSlot = (): ReactElement | undefined => {
    if (isTextOnlyLoading) {
      return undefined;
    }
    if (state.chargeForMakeTextEditable) {
      return getEditableLayersBadge();
    }
    return <BetaTag text={window.i18next.t('pmwjs_beta')} isDisabled={isDailyLimitReached} />;
  };

  const getEditableLayersTextSuffixElement = (): ReactElement | undefined => {
    if (isDailyLimitReached || isTextOnlyLoading) {
      return undefined;
    }
    return <PulsatingDot containerClassname={`spacing-m-l-2 _position-relative ${styles.editableTextPulsatingDot}`} />;
  };

  const getEditableImageItem = (id: string, text: string, layerDecompositionModel: LayerDecompositionModel): ActionMenuItem => {
    const isLoading = isMakeEditableLoading && runningLayerDecompositionModel === layerDecompositionModel;
    const isItemDisabled = isDisabled || (isMakeEditableLoading && runningLayerDecompositionModel !== layerDecompositionModel);
    return {
      id,
      icon: 'icon-ai-image',
      text,
      onClick: onImageLayeringClick(layerDecompositionModel),
      isLoading,
      isDisabled: isItemDisabled,
      customElement: isLoading ? undefined : <BetaTag text={window.i18next.t('pmwjs_experiment')} isDisabled={isItemDisabled} />,
    };
  };

  const getEditMenuItems = (): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      {
        id: 'edit-as-image',
        icon: 'icon-image',
        text: window.i18next.t('pmwjs_edit_as_image'),
        onClick: onOpenInEditor,
        isDisabled,
      },
    ];

    if (showEditableLayers) {
      items.push({
        id: 'editable-layers',
        icon: 'icon-prints-stack',
        textSuffixElement: getEditableLayersTextSuffixElement(),
        text: window.i18next.t('pmwjs_editable_text'),
        onClick: onEditableLayersClick,
        isLoading: isTextOnlyLoading,
        isDisabled: isDisabled || isRunningLayering,
        tooltip: !isEditMenuInModalMode && isDailyLimitReached ? window.i18next.t('pmwjs_editable_text_daily_limit_reached') : undefined,
        textClasses: isDailyLimitReached ? styles.dailyLimitReached : undefined,
        iconClasses: isDailyLimitReached ? styles.dailyLimitReached : undefined,
        customElement: getEditableLayersRightSlot(),
      });
    }

    if (isMieLayeringAvailable()) {
      items.push(getEditableImageItem('editable-image-flash', window.i18next.t('pmwjs_editable_image_flash'), LayerDecompositionModel.SEEDREAM_5_FLASH));
      items.push(getEditableImageItem('editable-image-pro', window.i18next.t('pmwjs_editable_image_pro'), LayerDecompositionModel.SEEDREAM_5_PRO));
    }

    return items;
  };

  const getMobileEditMenuRow = (): ReactElement => {
    return (
      <ControlledListItemType7
        id={'edit'}
        text={window.i18next.t('pmwjs_edit')}
        icon={'icon-pencil'}
        rightIcon={'icon-caret-right'}
        onClick={(_id, event): void => {
          event?.stopPropagation();
          setEditScreen(EditMenuScreen.EDIT);
          refreshDailyLimitOnMenuOpen();
        }}
        isDisabled={isDisabled}
        className={'radius-8'}
      />
    );
  };

  const getEditMenuRow = (): ReactElement => {
    if (isEditMenuInModalMode) {
      return getMobileEditMenuRow();
    }

    return (
      <ActionMenuFlyout
        items={getEditMenuItems()}
        infoNote={getEditableLayersInfoNote()}
        isDisabled={isDisabled}
        triggerText={window.i18next.t('pmwjs_edit')}
        triggerIcon="icon-pencil"
        onOpen={refreshDailyLimitOnMenuOpen}
      />
    );
  };

  const getEditSubmenu = ({isMobile = true}: GetEditSubmenuOptions = {}): ReactElement => {
    return <ActionMenu isMobile={isMobile} items={getEditMenuItems()} infoNote={getEditableLayersInfoNote()} />;
  };

  const getMoreOptionsModalHeaderProps = (): Partial<MoreOptionsModalProps> => {
    if (editScreen === EditMenuScreen.EDIT) {
      return {
        headerTitle: window.i18next.t('pmwjs_edit'),
        showBackButton: true,
        showCloseButton: false,
        closeModalOnBack: false,
        shouldCenterAlignTitle: true,
        onBack: (): void => {
          setEditScreen(EditMenuScreen.MAIN);
        },
      };
    }

    return {
      headerTitle: window.i18next.t('pmwjs_more_options'),
      showBackButton: false,
      showCloseButton: true,
      closeModalOnBack: true,
    };
  };

  return {
    editScreen,
    isEditSubmenuScreen: editScreen === EditMenuScreen.EDIT,
    isEditMenuInModalMode,
    showEditableLayers,
    isDailyLimitReached,
    refreshDailyLimitOnMenuOpen,
    getMobileEditMenuRow,
    getEditMenuRow,
    getEditSubmenu,
    getMoreOptionsModalHeaderProps,
  };
};
